<script>
import axios from "axios";
import Swal from "sweetalert2";
import { mapGetters } from "vuex";
import { Dropdown, Modal } from "bootstrap";

const API = process.env.VUE_APP_API_BASE_URL;
const modalInstances = new WeakMap();
const emptyForm = () => ({
  linkedUserId: null,
  name: "",
  contactName: "",
  email: "",
  phone: "",
  notes: "",
  status: "active",
});

export default {
  name: "AdmissionPartnersMgt",
  data() {
    return {
      summary: {
        activePartners: 0,
        uniqueVisitors: 0,
        registered: 0,
        schoolFeePaid: 0,
        registrationConversion: 0,
      },
      partners: [],
      academicSessions: [],
      programOfferings: [],
      filters: {
        search: "",
        status: "",
        entrySessionId: "",
        programOfferingId: "",
        startDate: "",
        endDate: "",
      },
      pagination: { page: 1, limit: 20, total: 0, pages: 1 },
      loading: false,
      saving: false,
      initialized: false,
      searchTimer: null,
      editingPartnerId: null,
      form: emptyForm(),
      selectedUser: null,
      userOptions: [],
      searchingUsers: false,
      userSearchTimer: null,
      selectedPartner: null,
      partnerApplications: [],
      loadingDetails: false,
    };
  },
  computed: {
    ...mapGetters(["hasPermission"]),
    canCreate() {
      return this.hasPermission("admissionPartners", "create");
    },
    canEdit() {
      return this.hasPermission("admissionPartners", "edit");
    },
    canDeactivate() {
      return this.hasPermission("admissionPartners", "deactivate");
    },
    canExport() {
      return this.hasPermission("admissionPartners", "export");
    },
    pageNumbers() {
      const start = Math.max(1, this.pagination.page - 2);
      const end = Math.min(this.pagination.pages, start + 4);
      return Array.from(
        { length: end - start + 1 },
        (_, index) => start + index
      );
    },
    publicBaseUrl() {
      return process.env.VUE_APP_MAIN_URL || "https://firstcitypoly.edu.ng";
    },
  },
  watch: {
    "filters.search"() {
      if (!this.initialized) return;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.refresh(1), 350);
    },
    "pagination.limit"() {
      if (this.initialized) this.refresh(1);
    },
  },
  async mounted() {
    modalInstances.set(this, {
      form: new Modal(this.$refs.partnerFormModal, {
        backdrop: "static",
        keyboard: false,
      }),
      details: new Modal(this.$refs.partnerDetailsModal, {
        backdrop: "static",
        keyboard: false,
      }),
    });
    this.$refs.partnerFormModal.addEventListener(
      "hidden.bs.modal",
      this.onFormModalHidden
    );
    this.$refs.partnerDetailsModal.addEventListener(
      "hidden.bs.modal",
      this.onDetailsModalHidden
    );
    await this.loadInitialData();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.userSearchTimer);
    this.$refs.partnerFormModal?.removeEventListener(
      "hidden.bs.modal",
      this.onFormModalHidden
    );
    this.$refs.partnerDetailsModal?.removeEventListener(
      "hidden.bs.modal",
      this.onDetailsModalHidden
    );
    const instances = modalInstances.get(this);
    instances?.form.dispose();
    instances?.details.dispose();
    modalInstances.delete(this);
  },
  methods: {
    requestParams(page = this.pagination.page) {
      return {
        page,
        limit: this.pagination.limit,
        ...Object.fromEntries(
          Object.entries(this.filters).filter(([, value]) => value !== "")
        ),
      };
    },
    async loadInitialData() {
      this.loading = true;
      try {
        const [sessions, offerings] = await Promise.all([
          axios.get(`${API}/academic-sessions`),
          axios.get(`${API}/program-offerings`),
        ]);
        this.academicSessions = Array.isArray(sessions.data)
          ? sessions.data
          : [];
        this.programOfferings = Array.isArray(offerings.data)
          ? offerings.data
          : [];
        this.initialized = true;
        await this.refresh(1);
      } catch (error) {
        this.showError(error, "Unable to load Admission Partners");
      } finally {
        this.loading = false;
      }
    },
    async refresh(page = this.pagination.page) {
      this.loading = true;
      try {
        const params = this.requestParams(page);
        const [partnersResponse, summaryResponse] = await Promise.all([
          axios.get(`${API}/admission-partners`, { params }),
          axios.get(`${API}/admission-partners/summary`, { params }),
        ]);
        this.partners = partnersResponse.data.data.partners || [];
        this.pagination = {
          ...this.pagination,
          ...(partnersResponse.data.data.pagination || {}),
        };
        this.summary = summaryResponse.data.data || this.summary;
      } catch (error) {
        this.showError(error, "Unable to refresh partner statistics");
      } finally {
        this.loading = false;
      }
    },
    async applyFilters() {
      await this.refresh(1);
      Dropdown.getInstance(this.$refs.filtersToggle)?.hide();
    },
    clearFilters() {
      this.initialized = false;
      this.filters = {
        search: "",
        status: "",
        entrySessionId: "",
        programOfferingId: "",
        startDate: "",
        endDate: "",
      };
      this.$nextTick(() => {
        this.initialized = true;
        this.refresh(1);
      });
    },
    offeringLabel(offering) {
      const program = offering.program?.name || "Programme";
      const type = offering.structure?.type?.type;
      const mode = offering.structure?.mode?.mode;
      return [program, type, mode].filter(Boolean).join(" — ");
    },
    statusLabel(status) {
      return String(status || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    statusClass(status) {
      return (
        {
          active: "text-bg-success",
          inactive: "text-bg-secondary",
          suspended: "text-bg-warning",
        }[status] || "text-bg-light"
      );
    },
    referralLink(partner) {
      return `${this.publicBaseUrl.replace(
        /\/$/,
        ""
      )}/apply?ref=${encodeURIComponent(partner.referralCode)}`;
    },
    async copyLink(partner) {
      try {
        await navigator.clipboard.writeText(this.referralLink(partner));
        Swal.fire({
          toast: true,
          position: "top-end",
          timer: 1800,
          showConfirmButton: false,
          icon: "success",
          title: "Referral link copied",
        });
      } catch (error) {
        this.showError(error, "Could not copy the referral link");
      }
    },
    openCreate() {
      this.editingPartnerId = null;
      this.form = emptyForm();
      this.selectedUser = null;
      this.userOptions = [];
      modalInstances.get(this)?.form.show();
    },
    openEdit(partner) {
      this.editingPartnerId = partner._id;
      this.form = {
        linkedUserId: partner.linkedUserId?._id || null,
        name: partner.name || "",
        contactName: partner.contactName || "",
        email: partner.email || "",
        phone: partner.phone || "",
        notes: partner.notes || "",
        status: partner.status,
      };
      this.selectedUser = partner.linkedUserId
        ? {
            userId: partner.linkedUserId._id,
            name: [
              partner.linkedUserId.firstName,
              partner.linkedUserId.otherName,
              partner.linkedUserId.lastName,
            ]
              .filter(Boolean)
              .join(" "),
            email: partner.linkedUserId.email,
            phone: partner.linkedUserId.phone,
            role: partner.linkedUserId.role,
            status: partner.linkedUserId.status,
          }
        : null;
      this.userOptions = this.selectedUser ? [this.selectedUser] : [];
      modalInstances.get(this)?.form.show();
    },
    closeForm() {
      if (this.saving) return;
      modalInstances.get(this)?.form.hide();
    },
    onFormModalHidden() {
      this.selectedUser = null;
      this.userOptions = [];
    },
    searchUsers(search) {
      clearTimeout(this.userSearchTimer);
      if (String(search || "").trim().length < 2) {
        this.userOptions = this.selectedUser ? [this.selectedUser] : [];
        return;
      }
      this.userSearchTimer = setTimeout(async () => {
        this.searchingUsers = true;
        try {
          const response = await axios.get(
            `${API}/admission-partners/user-options`,
            {
              params: {
                q: search.trim(),
                limit: 20,
                partnerId: this.editingPartnerId || undefined,
              },
            }
          );
          this.userOptions = response.data.data.users || [];
        } catch (error) {
          this.showError(error, "Unable to search users");
        } finally {
          this.searchingUsers = false;
        }
      }, 300);
    },
    selectUser(user) {
      if (!user) {
        this.form.linkedUserId = null;
        return;
      }
      if (user.alreadyLinked) return;
      this.form.linkedUserId = user.userId;
      this.form.name = user.name;
      this.form.contactName = user.name;
      this.form.email = user.email;
      this.form.phone = user.phone;
    },
    async savePartner() {
      this.saving = true;
      try {
        const payload = {
          ...this.form,
          linkedUserId: this.form.linkedUserId || null,
        };
        if (this.editingPartnerId) {
          await axios.patch(
            `${API}/admission-partners/${this.editingPartnerId}`,
            payload
          );
        } else {
          await axios.post(`${API}/admission-partners`, payload);
        }
        modalInstances.get(this)?.form.hide();
        await this.refresh(this.pagination.page);
        Swal.fire("Saved", "Admission Partner saved successfully.", "success");
      } catch (error) {
        this.showError(error, "Unable to save Admission Partner");
      } finally {
        this.saving = false;
      }
    },
    async changeStatus(partner, action) {
      const labels = {
        deactivate: "deactivate",
        suspend: "suspend",
        reactivate: "reactivate",
      };
      const result = await Swal.fire({
        title: `${this.statusLabel(labels[action])} partner?`,
        text:
          action === "reactivate"
            ? "New referral visits will be accepted again."
            : "New referral visits will no longer be attributed.",
        input: action === "reactivate" ? undefined : "textarea",
        inputLabel: action === "reactivate" ? undefined : "Reason (optional)",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.post(`${API}/admission-partners/${partner._id}/${action}`, {
          reason: result.value || undefined,
        });
        await this.refresh(this.pagination.page);
        Swal.fire("Updated", "Partner status updated.", "success");
      } catch (error) {
        this.showError(error, "Unable to update partner status");
      }
    },
    async viewFunnel(partner) {
      this.selectedPartner = partner;
      this.partnerApplications = [];
      this.loadingDetails = true;
      await this.$nextTick();
      modalInstances.get(this)?.details.show();
      try {
        const response = await axios.get(
          `${API}/admission-partners/${partner._id}/applications`,
          { params: { limit: 100 } }
        );
        this.partnerApplications = response.data.data.applications || [];
      } catch (error) {
        this.showError(error, "Unable to load referred applications");
      } finally {
        this.loadingDetails = false;
      }
    },
    closeDetails() {
      modalInstances.get(this)?.details.hide();
    },
    onDetailsModalHidden() {
      this.selectedPartner = null;
      this.partnerApplications = [];
    },
    progressLabel(progress) {
      if (!progress) return "Registered";
      const completed =
        progress.trackerStages?.filter((stage) => stage.completed) || [];
      const current = completed[completed.length - 1];
      return current?.label || "Registered";
    },
    applicantName(item) {
      const user = item.application?.userId || {};
      return (
        [user.lastName, user.firstName, user.otherName]
          .filter(Boolean)
          .join(" ") || "Applicant"
      );
    },
    async exportPartners() {
      try {
        const response = await axios.get(`${API}/admission-partners/export`, {
          params: this.requestParams(1),
          responseType: "blob",
        });
        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.download = `admission-partners-${new Date()
          .toISOString()
          .slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.showError(error, "Unable to export partners");
      }
    },
    showError(error, fallback) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || error?.message || fallback,
        "error"
      );
    },
  },
};
</script>

<template>
  <section class="container-fluid py-4 admission-partners">
    <div
      class="d-flex flex-wrap justify-content-between align-items-center text-start gap-3 mb-4"
    >
      <div>
        <h2 class="fw-bold mb-1">Admission Partners</h2>
        <p class="text-muted mb-0">
          Manage referral links and follow applicants through the admission
          process.
        </p>
      </div>
      <div class="d-flex gap-2">
        <button
          v-if="canExport"
          class="btn btn-outline-dark"
          @click="exportPartners"
        >
          <i class="bi bi-download me-2"></i>Export
        </button>
        <button v-if="canCreate" class="btn btn-dark" @click="openCreate">
          <i class="bi bi-person-plus me-2"></i>New Partner
        </button>
      </div>
    </div>

    <div class="row g-3 mb-5">
      <div
        class="col-12 col-sm-6 col-xl-3"
        v-for="card in [
          {
            label: 'Active Partners',
            value: summary.activePartners,
            icon: 'bi-people',
            color: 'primary',
          },
          {
            label: 'Unique Referral Visitors',
            value: summary.uniqueVisitors,
            icon: 'bi-cursor',
            color: 'info',
          },
          {
            label: 'Registered Applicants',
            value: summary.registered,
            icon: 'bi-person-check',
            color: 'warning',
          },
          {
            label: 'School Fee Paid',
            value: summary.schoolFeePaid,
            icon: 'bi-mortarboard',
            color: 'success',
          },
        ]"
        :key="card.label"
      >
        <div class="card border-0 shadow-sm h-100">
          <div
            class="card-body d-flex justify-content-between align-items-center"
          >
            <div>
              <p class="text-muted small mb-1">{{ card.label }}</p>
              <h3 class="fw-bold mb-0">{{ card.value || 0 }}</h3>
            </div>
            <span :class="`stat-icon text-bg-${card.color}`"
              ><i :class="`bi ${card.icon}`"></i
            ></span>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-4 g-3">
      <div class="col-md-7">
        <div class="input-group">
          <span class="input-group-text bg-white">
            <i class="bi bi-search"></i>
          </span>
          <input
            v-model="filters.search"
            type="search"
            class="form-control border-start-0"
            placeholder="Search by partner, email, phone, or referral code"
            @keyup.enter="refresh(1)"
          />
        </div>
      </div>
      <div class="col-md-5 d-flex justify-content-md-end gap-2">
        <div class="dropdown">
          <button
            ref="filtersToggle"
            class="btn btn-sm btn-outline-secondary"
            type="button"
            data-bs-toggle="dropdown"
            data-bs-auto-close="outside"
            aria-expanded="false"
          >
            <i class="bi bi-funnel me-1"></i>Filters
          </button>
          <div class="dropdown-menu dropdown-menu-end p-3 partner-filter-menu">
            <div class="mb-3 d-flex justify-content-between align-items-center">
              <span class="fw-semibold small">Advanced Filter</span>
              <button
                class="btn btn-link btn-sm p-0"
                type="button"
                @click="clearFilters"
              >
                clear all
              </button>
            </div>

            <div class="mb-3">
              <label class="small text-muted mb-1 d-block">Status</label>
              <select
                v-model="filters.status"
                class="form-select form-select-sm"
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="small text-muted mb-1 d-block">Entry session</label>
              <select
                v-model="filters.entrySessionId"
                class="form-select form-select-sm"
              >
                <option value="">All sessions</option>
                <option
                  v-for="session in academicSessions"
                  :key="session._id"
                  :value="session._id"
                >
                  {{ session.sessionYear }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label class="small text-muted mb-1 d-block"
                >Programme offering</label
              >
              <select
                v-model="filters.programOfferingId"
                class="form-select form-select-sm"
              >
                <option value="">All programmes</option>
                <option
                  v-for="offering in programOfferings"
                  :key="offering._id"
                  :value="offering._id"
                >
                  {{ offeringLabel(offering) }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label class="small text-muted mb-1 d-block"
                >Referral date range</label
              >
              <div class="row g-2">
                <div class="col-6">
                  <input
                    v-model="filters.startDate"
                    type="date"
                    class="form-control form-control-sm"
                    aria-label="Referral start date"
                  />
                </div>
                <div class="col-6">
                  <input
                    v-model="filters.endDate"
                    type="date"
                    class="form-control form-control-sm"
                    aria-label="Referral end date"
                  />
                </div>
              </div>
            </div>

            <button
              class="btn btn-dark btn-sm w-100"
              type="button"
              :disabled="loading"
              @click="applyFilters"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              Apply filters
            </button>
          </div>
        </div>

        <button
          class="btn btn-sm btn-primary"
          type="button"
          :disabled="loading"
          @click="refresh(pagination.page)"
        >
          <i class="bi bi-arrow-clockwise me-1"></i>Refresh
        </button>
      </div>
    </div>

    <div class="card border-0 shadow-sm">
      <div class="card-body p-0">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border" role="status"></div>
          <p class="text-muted mt-2">Loading partners...</p>
        </div>
        <div v-else-if="!partners.length" class="text-center py-5">
          <i class="bi bi-people fs-1 text-muted"></i>
          <h5 class="mt-3">No Admission Partners found</h5>
          <p class="text-muted">
            Create a partner or change the current filters.
          </p>
        </div>
        <div v-else class="">
          <table class="table table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>Partner</th>
                <th>Referral link</th>
                <th>Status</th>
                <th class="text-center">Visitors</th>
                <th class="text-center">Registered</th>
                <th class="text-center">Admitted</th>
                <th class="text-center">Tuition</th>
                <th class="text-center">Conversion</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="partner in partners" :key="partner._id">
                <td>
                  <div class="fw-semibold">{{ partner.name }}</div>
                  <small class="text-muted">{{ partner.email }}</small>
                  <div v-if="partner.linkedUserId" class="small text-primary">
                    <i class="bi bi-link-45deg"></i> Linked
                    {{ statusLabel(partner.linkedUserId.role) }} user
                  </div>
                </td>
                <td>
                  <div class="input-group input-group-sm link-field">
                    <input
                      class="form-control"
                      readonly
                      :value="referralLink(partner)"
                    /><button
                      class="btn btn-outline-secondary"
                      title="Copy link"
                      @click="copyLink(partner)"
                    >
                      <i class="bi bi-copy"></i>
                    </button>
                  </div>
                  <small class="text-muted">{{ partner.referralCode }}</small>
                </td>
                <td>
                  <span class="badge" :class="statusClass(partner.status)">{{
                    statusLabel(partner.status)
                  }}</span>
                </td>
                <td class="text-center">
                  {{ partner.metrics.uniqueVisitors || 0 }}
                </td>
                <td class="text-center">
                  {{ partner.metrics.registered || 0 }}
                </td>
                <td class="text-center">
                  {{ partner.metrics.admissionOffered || 0 }}
                </td>
                <td class="text-center">
                  {{ partner.metrics.schoolFeePaid || 0 }}
                </td>
                <td class="text-center">
                  {{ partner.metrics.registrationConversion || 0 }}%
                </td>
                <td>
                  <div class="dropdown">
                    <button
                      class="btn btn-sm btn-outline-dark dropdown-toggle"
                      data-bs-toggle="dropdown"
                    >
                      Manage
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end">
                      <li>
                        <button
                          class="dropdown-item"
                          @click="viewFunnel(partner)"
                        >
                          <i class="bi bi-bar-chart me-2"></i>View funnel
                        </button>
                      </li>
                      <li v-if="canEdit">
                        <button
                          class="dropdown-item"
                          @click="openEdit(partner)"
                        >
                          <i class="bi bi-pencil me-2"></i>Edit
                        </button>
                      </li>
                      <li v-if="canDeactivate && partner.status === 'active'">
                        <button
                          class="dropdown-item text-warning"
                          @click="changeStatus(partner, 'suspend')"
                        >
                          <i class="bi bi-pause-circle me-2"></i>Suspend
                        </button>
                      </li>
                      <li v-if="canDeactivate && partner.status === 'active'">
                        <button
                          class="dropdown-item text-danger"
                          @click="changeStatus(partner, 'deactivate')"
                        >
                          <i class="bi bi-x-circle me-2"></i>Deactivate
                        </button>
                      </li>
                      <li v-if="canDeactivate && partner.status !== 'active'">
                        <button
                          class="dropdown-item text-success"
                          @click="changeStatus(partner, 'reactivate')"
                        >
                          <i class="bi bi-check-circle me-2"></i>Reactivate
                        </button>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div
        v-if="pagination.total"
        class="card-footer bg-white d-flex flex-wrap justify-content-between align-items-center gap-2"
      >
        <small class="text-muted"
          >{{ pagination.total }} partner{{
            pagination.total === 1 ? "" : "s"
          }}</small
        >
        <div class="d-flex align-items-center gap-2">
          <select
            v-model.number="pagination.limit"
            class="form-select form-select-sm"
            style="width: 82px"
          >
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
          </select>
          <nav class="py-0">
            <ul class="pagination pagination-sm mb-0">
              <li
                class="page-item"
                :class="{ disabled: pagination.page === 1 }"
              >
                <button class="page-link" @click="refresh(pagination.page - 1)">
                  ‹
                </button>
              </li>
              <li
                v-for="page in pageNumbers"
                :key="page"
                class="page-item"
                :class="{ active: page === pagination.page }"
              >
                <button class="page-link" @click="refresh(page)">
                  {{ page }}
                </button>
              </li>
              <li
                class="page-item"
                :class="{ disabled: pagination.page === pagination.pages }"
              >
                <button class="page-link" @click="refresh(pagination.page + 1)">
                  ›
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <div
      ref="partnerFormModal"
      class="modal fade"
      tabindex="-1"
      aria-labelledby="partnerFormModalLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div
        class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg partner-form-dialog"
      >
        <div class="modal-content">
          <div
            class="modal-header bg-white d-flex justify-content-between align-items-center"
          >
            <div>
              <h5 id="partnerFormModalLabel" class="modal-title mb-0">
                {{ editingPartnerId ? "Edit" : "New" }} Admission Partner
              </h5>
              <small class="text-muted"
                >Link an existing user or enter the partner manually.</small
              >
            </div>
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              :disabled="saving"
              @click="closeForm"
            ></button>
          </div>
          <form
            class="partner-form d-flex flex-column overflow-hidden"
            @submit.prevent="savePartner"
          >
            <div class="modal-body">
              <div class="row g-3 text-start">
                <div class="col-md-8">
                  <label class="form-label">
                    Existing user <span class="text-muted">(optional)</span>
                  </label>
                  <v-select
                    v-model="selectedUser"
                    :options="userOptions"
                    label="name"
                    :filterable="false"
                    :loading="searchingUsers"
                    :selectable="(user) => !user.alreadyLinked"
                    placeholder="Search name, email, phone, application, matric or staff number"
                    @search="searchUsers"
                    @update:modelValue="selectUser"
                  >
                    <template #option="user">
                      <div>
                        <strong>{{ user.name }}</strong>
                        <div class="small text-muted">
                          {{ user.email }} · {{ statusLabel(user.role) }} ·
                          {{ statusLabel(user.status)
                          }}<span v-if="user.identifier">
                            · {{ user.identifier }}</span
                          >
                        </div>
                        <div
                          v-if="user.alreadyLinked"
                          class="small text-danger"
                        >
                          Already linked to {{ user.linkedPartnerName }}
                        </div>
                      </div>
                    </template>
                  </v-select>
                  <div v-if="selectedUser" class="form-text text-primary">
                    <i class="bi bi-link-45deg"></i> This associates the partner
                    with the user but does not change their role or grant
                    partner login access.
                  </div>
                  <div
                    v-if="selectedUser && selectedUser.status !== 'active'"
                    class="form-text text-warning"
                  >
                    <i class="bi bi-exclamation-triangle me-1"></i>The linked
                    User account is {{ statusLabel(selectedUser.status) }}. This
                    does not automatically deactivate the partner.
                  </div>
                </div>
                <div v-if="!editingPartnerId" class="col-md-4">
                  <label class="form-label">Initial status</label>
                  <select v-model="form.status" class="form-select">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label">
                    Partner name <span class="text-danger">*</span></label
                  >
                  <input
                    v-model.trim="form.name"
                    class="form-control text-capitalize"
                    placeholder="e.g. person, business, school, agency, or organisation"
                    required
                    maxlength="160"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Contact name</label
                  ><input
                    v-model.trim="form.contactName"
                    class="form-control text-capitalize"
                    placeholder="e.g. John Doe"
                    maxlength="160"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label"
                    >Email <span class="text-danger">*</span></label
                  ><input
                    v-model.trim="form.email"
                    type="email"
                    placeholder="e.g. mail@domain.com"
                    class="form-control"
                    required
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label"
                    >Phone <span class="text-danger">*</span></label
                  >
                  <input
                    v-model.trim="form.phone"
                    type="tel"
                    class="form-control"
                    placeholder="e.g. +234 801 234 5678"
                    required
                  />
                </div>
                <div class="col-12">
                  <label class="form-label">Notes</label>
                  <textarea
                    v-model.trim="form.notes"
                    class="form-control"
                    rows="3"
                    maxlength="2000"
                  ></textarea>
                </div>
              </div>
            </div>
            <div class="modal-footer bg-white d-flex justify-content-end gap-2">
              <button
                type="button"
                class="btn btn-outline-secondary"
                :disabled="saving"
                @click="closeForm"
              >
                Cancel</button
              ><button class="btn btn-dark" :disabled="saving">
                <span
                  v-if="saving"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ saving ? "Saving..." : "Save Partner" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div
      ref="partnerDetailsModal"
      class="modal fade"
      tabindex="-1"
      aria-labelledby="partnerDetailsModalLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div
        class="modal-dialog modal-dialog-centered modal-dialog-scrollable partner-details-dialog"
      >
        <div v-if="selectedPartner" class="modal-content">
          <div
            class="modal-header bg-white d-flex justify-content-between align-items-center"
          >
            <div>
              <h5 id="partnerDetailsModalLabel" class="modal-title mb-0">
                {{ selectedPartner.name }} funnel
              </h5>
              <small class="text-muted"
                >Attribution ends at School Fee Paid.</small
              >
            </div>
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              @click="closeDetails"
            ></button>
          </div>
          <div class="modal-body">
            <div class="funnel-grid mb-4">
              <div
                v-for="stage in [
                  { label: 'Registered', key: 'registered' },
                  { label: 'Portal Fee', key: 'portalFeePaid' },
                  {
                    label: 'Application Form',
                    key: 'applicationFormSubmitted',
                  },
                  { label: 'Admission', key: 'admissionOffered' },
                  { label: 'Acceptance Fee', key: 'acceptanceFeePaid' },
                  { label: 'Clearance', key: 'clearanceCompleted' },
                  { label: 'Administrative Fee', key: 'administrativeFeePaid' },
                  { label: 'School Fee', key: 'schoolFeePaid' },
                ]"
                :key="stage.key"
                class="funnel-stage"
              >
                <span>{{ stage.label }}</span
                ><strong>{{ selectedPartner.metrics[stage.key] || 0 }}</strong>
              </div>
            </div>
            <h6 class="fw-bold">Referred applicants</h6>
            <div v-if="loadingDetails" class="text-center py-4">
              <div class="spinner-border spinner-border-sm"></div>
            </div>
            <div
              v-else-if="!partnerApplications.length"
              class="alert alert-light border"
            >
              No registered applicants for this partner yet.
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Application</th>
                    <th>Programme</th>
                    <th>Current completed stage</th>
                    <th>Attributed</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in partnerApplications"
                    :key="item.referralId"
                  >
                    <td>
                      {{ applicantName(item) }}
                      <div class="small text-muted">
                        {{ item.application.userId?.email }}
                      </div>
                    </td>
                    <td>{{ item.application.applicationNumber }}</td>
                    <td>
                      {{
                        item.application.programOfferingId?.program?.name || "—"
                      }}
                    </td>
                    <td>
                      <span class="badge text-bg-light border">{{
                        progressLabel(item.progress)
                      }}</span>
                    </td>
                    <td>
                      {{ new Date(item.attributedAt).toLocaleDateString() }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer bg-white">
            <button class="btn btn-dark" @click="closeDetails">Close</button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.admission-partners {
  min-height: 75vh;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
}

.link-field {
  min-width: 260px;
}

.partner-filter-menu {
  width: 300px;
}

.partner-form-dialog {
  max-width: 720px;
}

.partner-form {
  flex: 1 1 auto;
  min-height: 0;
}

.partner-form .modal-body {
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.partner-details-dialog {
  max-width: 1100px;
}

.funnel-grid {
  display: grid;
  grid-template-columns: repeat(8, minmax(110px, 1fr));
  overflow-x: auto;
  gap: 0.5rem;
}

.funnel-stage {
  min-width: 110px;
  padding: 0.85rem;
  border-radius: 0.75rem;
  background: #f8f9fa;
  border-left: 4px solid #181c1f;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.funnel-stage span {
  color: #6c757d;
  font-size: 0.75rem;
}

.funnel-stage strong {
  font-size: 1.3rem;
}

@media (max-width: 575.98px) {
  .partner-filter-menu {
    min-width: min(340px, calc(100vw - 2rem));
  }
}
</style>
